import React, { useState, useEffect } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';
import './ModuleTopicsTargetsModal.css';

/**
 * Module Topics Targets Modal Component - Exact Angular Implementation
 * Handles setting target levels for topics and classes
 */
const ModuleTopicsTargetsModal = ({ data, onClose, onSave }) => {
  const [moduleTopics, setModuleTopics] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [topicToChange, setTopicToChange] = useState([]);
  const [classToChange, setClassToChange] = useState([]);

  // Helper function to encode token using AES encryption (exact match to Angular)
  const encodeToken = (token) => {
    return token.toString();
  };

  // Helper function to get current user ID
  const getCurrentUserId = () => {
    if (window.CookieUtilities) {
      return new window.CookieUtilities().getCookie("userID");
    }
    return null;
  };

  // Helper function to convert date (matches Angular)
  const getDate = (dateString) => {
    if (!dateString) return new Date();
    if (dateString.includes('/Date(')) {
      const timestamp = parseInt(dateString.match(/\d+/)[0]);
      return new Date(timestamp);
    }
    return new Date(dateString);
  };

  // Load module topics and targets (exact Angular implementation)
  const loadModuleTopicsTargets = async () => {
    if (!data.moduleSemesterId) return;

    setLoading(true);
    setError(null);

    try {
      const token = getTokenForRequest();
      const userID = getCurrentUserId();

      console.log('Loading module topics for moduleSemesterId:', data.moduleSemesterId);

      const encodedToken = encodeToken(token);

      // Call both APIs in parallel (like Angular $q.all)
      const [classesResponse, lessonPlanResponse] = await Promise.all([
        fetch('./webservices/Organization.svc/GetClasses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: encodedToken,
            modulesemesterid: data.moduleSemesterId
          }),
          credentials: 'include',
        }),
        fetch('./webservices/Organization.svc/GetLessonPlanWeeks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: encodedToken,
            modulesemesterid: data.moduleSemesterId
          }),
          credentials: 'include',
        })
      ]);

      if (!classesResponse.ok || !lessonPlanResponse.ok) {
        const classesError = classesResponse.ok ? '' : `Classes API: ${classesResponse.status} ${classesResponse.statusText}`;
        const lessonPlanError = lessonPlanResponse.ok ? '' : `LessonPlan API: ${lessonPlanResponse.status} ${lessonPlanResponse.statusText}`;
        throw new Error(`API Error - ${classesError} ${lessonPlanError}`.trim());
      }

      const classesData = await classesResponse.json();
      const lessonPlanData = await lessonPlanResponse.json();

      console.log('Raw API data:');
      console.log('Classes data:', classesData);
      console.log('Lesson plan data:', lessonPlanData);

      if (classesData && lessonPlanData) {
        const lessonPlans = lessonPlanData;
        const classesInfo = classesData;

        // Process classes (match Angular logic)
        const processedClasses = classesInfo.map(moduleClass => {
          // Set educator coordinator flags
          moduleClass.Educators.forEach(educator => {
            educator.isCoordinator = educator.ID === userID;
          });

          // Check if current user can edit this class
          moduleClass.Editable = moduleClass.Educators.some(educator => 
            educator.ID === userID
          );

          return moduleClass;
        });

        // Process topics (exact Angular logic)
        const processedTopics = [];
        let topicsHasDuplicate = false;

        lessonPlans.forEach(lessonPlan => {
          if (lessonPlan.Topics && lessonPlan.Topics.length > 0) {
            lessonPlan.Topics.forEach(topic => {
              if (topic.HasDuplicateTopicInSemester) {
                topicsHasDuplicate = true;
              }

              // FIXED: Initialize target level structure with default value of "4"
              topic.TargetLevel = {
                Key: topic.TopicID,
                Value: {
                  ID: 0,
                  ModuleSemesterID: topic.ModuleSemesterID,
                  TargetLevel: "4", // Default Expert level
                  TopicID: topic.TopicID,
                  UpdatedBy: "0",
                  UpdatedTime: topic.StartDate
                }
              };

              // FIXED: Check for existing module-wide target level from MSGoals
              if (lessonPlan.MSGoals && lessonPlan.MSGoals.length > 0) {
                const existingMSGoal = lessonPlan.MSGoals.find(goal => goal.Key === topic.TopicID);
                if (existingMSGoal && existingMSGoal.Value) {
                  // Use the existing MS goal data
                  topic.TargetLevel = {
                    Key: existingMSGoal.Key,
                    Value: {
                      ...existingMSGoal.Value,
                      TargetLevel: existingMSGoal.Value.TargetLevel.toString() // Convert to string
                    }
                  };
                }
              }

              console.log(`Topic ${topic.TopicName} (ID: ${topic.TopicID}) - Module target level:`, topic.TargetLevel.Value.TargetLevel);

              // FIXED: Initialize target levels array for classes
              topic.TargetLevels = [];

              // FIXED: Process class goals using the correct data structure
              processedClasses.forEach(moduleClass => {
                // Look for class goals for this topic
                let classGoalData = null;
                
                if (lessonPlan.ClassGoals && lessonPlan.ClassGoals.length > 0) {
                  // Find the ClassGoal entry for this topic
                  const classGoalEntry = lessonPlan.ClassGoals.find(goal => goal.Key === topic.TopicID);
                  
                  if (classGoalEntry && classGoalEntry.Value && Array.isArray(classGoalEntry.Value)) {
                    // Find the specific class goal for this class within the Value array
                    classGoalData = classGoalEntry.Value.find(classGoal => 
                      classGoal.ClassID === moduleClass.ID
                    );
                  }
                }

                if (classGoalData) {
                  // Create class goal object using existing data
                  const classGoal = {
                    ID: moduleClass.ID,
                    Key: topic.TopicID,
                    Value: {
                      ...classGoalData,
                      TargetLevel: classGoalData.TargetLevel.toString() // Convert to string
                    },
                    Editable: moduleClass.Editable || data.isCoordinator,
                    NeedAccept: false
                  };

                  // Check if class needs to accept coordinator's changes
                  if ((classGoal.Value.TargetLevel !== topic.TargetLevel.Value.TargetLevel) && 
                      (getDate(classGoal.Value.UpdatedTime) < getDate(topic.TargetLevel.Value.UpdatedTime))) {
                    classGoal.NeedAccept = true;
                  }

                  topic.TargetLevels.push(classGoal);
                  console.log(`  Class ${moduleClass.Name} (ID: ${moduleClass.ID}) - Target level:`, classGoal.Value.TargetLevel, `(Editable: ${classGoal.Editable})`);
                } else {
                  console.log(`  Class ${moduleClass.Name} (ID: ${moduleClass.ID}) - No class goal data`);
                }
              });

              processedTopics.push(topic);
            });
          }
        });

        setClasses(processedClasses);
        setModuleTopics(processedTopics);
        setTopicToChange([]);
        setClassToChange([]);

        console.log('Processed data summary:');
        console.log('- Classes:', processedClasses.length);
        console.log('- Topics:', processedTopics.length);
        console.log('- Topics with class goals:', processedTopics.filter(t => t.TargetLevels.length > 0).length);
      }
    } catch (err) {
      console.error('Error loading module topics:', err);
      setError(err.message || 'Error retrieving module semester info');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModuleTopicsTargets();
  }, [data.moduleSemesterId]);

  // Add topic to change list
  const addTopicToChangeList = (topic) => {
    setTopicToChange(prev => {
      const exists = prev.find(t => t.TopicID === topic.TopicID);
      if (!exists) {
        return [...prev, topic];
      }
      return prev;
    });
  };

  // Add class to change list
  const addClassToChangeList = (classGoal, topic) => {
    classGoal.topic = topic;
    setClassToChange(prev => {
      const exists = prev.find(c => c.ID === classGoal.ID && c.topic.TopicID === topic.TopicID);
      if (!exists) {
        return [...prev, classGoal];
      }
      return prev;
    });
  };

  // Update topic target level
  const updateTopicTarget = (topicId, targetLevel) => {
    setModuleTopics(prevTopics =>
      prevTopics.map(topic => {
        if (topic.TopicID === topicId) {
          const updatedTopic = {
            ...topic,
            TargetLevel: {
              ...topic.TargetLevel,
              Value: {
                ...topic.TargetLevel.Value,
                TargetLevel: targetLevel.toString()
              }
            }
          };
          addTopicToChangeList(updatedTopic);
          return updatedTopic;
        }
        return topic;
      })
    );
  };

  // Update class target level
  const updateClassTarget = (topicId, classId, targetLevel) => {
    setModuleTopics(prevTopics =>
      prevTopics.map(topic => {
        if (topic.TopicID === topicId) {
          const updatedTopic = {
            ...topic,
            TargetLevels: topic.TargetLevels.map(classGoal => {
              if (classGoal.ID === classId) {
                const updatedClassGoal = {
                  ...classGoal,
                  Value: {
                    ...classGoal.Value,
                    TargetLevel: targetLevel.toString()
                  }
                };
                addClassToChangeList(updatedClassGoal, topic);
                return updatedClassGoal;
              }
              return classGoal;
            })
          };
          return updatedTopic;
        }
        return topic;
      })
    );
  };

  // Accept topic level change
  const acceptTopicLevel = async (topic, moduleClass) => {
    try {
      const token = getTokenForRequest();
      const encodedToken = encodeToken(token);
      
      const response = await fetch('./webservices/Organization.svc/classgoal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: encodedToken,
          classID: moduleClass.ID,
          topicID: topic.TopicID,
          targetLevel: topic.TargetLevel.Value.TargetLevel
        }),
        credentials: 'include',
      });

      if (response.ok) {
        // Update state to reflect acceptance
        setModuleTopics(prevTopics =>
          prevTopics.map(t => {
            if (t.TopicID === topic.TopicID) {
              return {
                ...t,
                TargetLevels: t.TargetLevels.map(cg => {
                  if (cg.ID === moduleClass.ID) {
                    return {
                      ...cg,
                      Value: {
                        ...cg.Value,
                        TargetLevel: topic.TargetLevel.Value.TargetLevel
                      },
                      NeedAccept: false
                    };
                  }
                  return cg;
                })
              };
            }
            return t;
          })
        );
      }
    } catch (err) {
      console.error('Error accepting topic level:', err);
    }
  };

  // Reject topic level change
  const rejectTopicLevel = async (topic, moduleClass) => {
    try {
      const token = getTokenForRequest();
      const encodedToken = encodeToken(token);
      
      const response = await fetch('./webservices/Organization.svc/classgoal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: encodedToken,
          classID: moduleClass.ID,
          topicID: topic.TopicID,
          targetLevel: moduleClass.Value.TargetLevel
        }),
        credentials: 'include',
      });

      if (response.ok) {
        // Update state to reflect rejection
        setModuleTopics(prevTopics =>
          prevTopics.map(t => {
            if (t.TopicID === topic.TopicID) {
              return {
                ...t,
                TargetLevels: t.TargetLevels.map(cg => {
                  if (cg.ID === moduleClass.ID) {
                    return {
                      ...cg,
                      NeedAccept: false
                    };
                  }
                  return cg;
                })
              };
            }
            return t;
          })
        );
      }
    } catch (err) {
      console.error('Error rejecting topic level:', err);
    }
  };

  // Save module topics targets (exact Angular implementation)
  const setModuleTopicsTargets = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage('');

    try {
      const token = getTokenForRequest();
      const encodedToken = encodeToken(token);
      const processQ = [];

      console.log('Saving changes - Topics to change:', topicToChange.length, 'Classes to change:', classToChange.length);

      // Add topic changes
      topicToChange.forEach(topic => {
        processQ.push(
          fetch('./webservices/Organization.svc/msgoal', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              token: encodedToken,
              moduleSemesterID: data.moduleSemesterId,
              topicID: topic.TargetLevel.Key,
              targetLevel: topic.TargetLevel.Value.TargetLevel
            }),
            credentials: 'include',
          })
        );
      });

      // Add class changes
      classToChange.forEach(mclass => {
        processQ.push(
          fetch('./webservices/Organization.svc/classgoal', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              token: encodedToken,
              classID: mclass.ID,
              topicID: mclass.topic.TopicID,
              targetLevel: mclass.Value.TargetLevel
            }),
            credentials: 'include',
          })
        );
      });

      // Execute all API calls
      const responses = await Promise.all(processQ);
      
      // Check if all responses are OK
      const failedResponses = responses.filter(response => !response.ok);
      if (failedResponses.length > 0) {
        throw new Error(`Some save operations failed: ${failedResponses.length}/${responses.length}`);
      }

      // Show success message (matches Angular)
      setSuccessMessage('Topic Targets Saved');
      setTopicToChange([]);
      setClassToChange([]);

      console.log('All changes saved successfully');
      
      // Reload data to reflect the latest saved difficulty levels
      await loadModuleTopicsTargets();
      
    } catch (err) {
      console.error('Error saving target levels:', err);
      setError('Failed to Set Target Levels.');
      setTopicToChange([]);
      setClassToChange([]);
    } finally {
      setLoading(false);
    }
  };

  // Render status indicator
  const renderStatusIndicator = (hasStatus) => {
    return (
      <div className="status-indicator-container">
        <span className={`status-indicator ${hasStatus ? 'has-status' : 'no-status'}`}></span>
      </div>
    );
  };

  // Loading state
  if (loading && moduleTopics.length === 0) {
    return (
      <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', marginTop: '50px' }}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Set Module Topic Levels</h4>
              <button type="button" className="btn-close-x" onClick={onClose}>×</button>
            </div>
            <div className="modal-body text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading module topics...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', marginTop: '50px' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Set Module Topic Levels</h4>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <button type="button" className="btn-close-x" onClick={() => setError(null)}>×</button>
                <strong>{error}</strong>
              </div>
            )}

            {successMessage && (
              <div className="alert alert-warning alert-dismissible fade show" role="alert">
                <button type="button" className="btn-close-x" onClick={() => setSuccessMessage('')}>×</button>
                <strong>{successMessage}</strong>
              </div>
            )}

            <form className="form form-inline">
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Topic</th>
                      <th className="deployed-other-module-col">
                        Deployed in Other Module
                        <br />
                        <small style={{ color: 'grey', fontSize: '10px' }}>
                          This topic is deployed in the other modules in this semester, highest goal target will be used for each student.
                        </small>
                      </th>
                      <th>Evaluation</th>
                      <th>Duration(m)</th>
                      <th>Module Coordinator</th>
                      {classes.map(cls => (
                        <th key={cls.ID}>
                          {cls.Name} - Educator{cls.Educators && cls.Educators.length > 1 ? 's' : ''}:
                          <br />
                          {cls.Educators && cls.Educators.map((educator, index) => (
                            <span key={index} className="educator" style={{ marginLeft: '5px' }}>
                              <strong>{educator.Firstname || educator.DisplayName}</strong>
                              <br />
                            </span>
                          ))}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {moduleTopics.map(topic => (
                      <tr key={topic.TopicID}>
                        <td>{topic.TopicName}</td>
                        <td className="deployed-other-module-col" style={{ textAlign: 'center' }}>
                          {renderStatusIndicator(topic.HasDuplicateTopicInSemester)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {renderStatusIndicator(topic.HasEvaluation && !topic.NoEvaluationQuestions)}
                        </td>
                        <td>{topic.DurationInMinutes || '-'}</td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={topic.TargetLevel?.Value?.TargetLevel || '4'}
                            onChange={(e) => updateTopicTarget(topic.TopicID, e.target.value)}
                            disabled={!data.isCoordinator}
                          >
                            <option value="1">Beginner</option>
                            <option value="2">Advanced Beginner</option>
                            <option value="3">Competent</option>
                            <option value="4">Expert</option>
                          </select>
                        </td>
                        {classes.map(cls => {
                          // Find the class goal for this class
                          const classGoal = topic.TargetLevels?.find(cg => cg.ID === cls.ID);
                          
                          // Only show dropdown if classGoal exists (has actual data)
                          if (!classGoal) {
                            return <td key={cls.ID}></td>; // Empty cell if no goal data
                          }
                          
                          const canEdit = classGoal.Editable;
                          
                          return (
                            <td key={cls.ID} className={classGoal.Rejected && data.isCoordinator ? 'target-rejected' : ''}>
                              <select
                                className="form-select form-select-sm"
                                value={classGoal.Value?.TargetLevel || '4'}
                                onChange={(e) => updateClassTarget(topic.TopicID, cls.ID, e.target.value)}
                                disabled={!canEdit || classGoal.NeedAccept}
                              >
                                <option value="1">Beginner</option>
                                <option value="2">Advanced Beginner</option>
                                <option value="3">Competent</option>
                                <option value="4">Expert</option>
                              </select>
                              
                              {/* Accept or Reject Change */}
                              {canEdit && classGoal.NeedAccept && (
                                <div className="form-group mt-2">
                                  <label className="form-label small">
                                    MC changed target to {' '}
                                    <span className={`target-level-${topic.TargetLevel?.Value?.TargetLevel}`}>
                                      {topic.TargetLevel?.Value?.TargetLevel === '1' ? 'Beginner' :
                                       topic.TargetLevel?.Value?.TargetLevel === '2' ? 'Advanced Beginner' :
                                       topic.TargetLevel?.Value?.TargetLevel === '3' ? 'Competent' : 'Expert'}
                                    </span>, do you accept the change?
                                  </label>
                                  <div className="d-flex gap-1 mt-1">
                                    <button
                                      type="button"
                                      className="btn btn-success btn-sm"
                                      onClick={() => acceptTopicLevel(topic, classGoal)}
                                    >
                                      Accept
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      onClick={() => rejectTopicLevel(topic, classGoal)}
                                    >
                                      Reject
                                    </button>
                                  </div>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </form>

            <div className="clearfix"></div>
          </div>

          <div className="modal-footer">
            <form></form>
            <button
              type="button"
              className="btn btn-success"
              onClick={setModuleTopicsTargets}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleTopicsTargetsModal; 